import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { useMsal } from "@azure/msal-react";
import { AgentInfo } from "./api/models";
import { getAgentsApi } from "./api";
import { useLogin, getToken } from "./authConfig";

interface AgentContextType {
    agents: AgentInfo[];
    loading: boolean;
    error: string | null;
    refresh: () => void;
    currentAgent: string;
    setCurrentAgent: (agent: string) => void;
}

const AgentContext = createContext<AgentContextType>({
    agents: [],
    loading: true,
    error: null,
    refresh: () => {},
    currentAgent: "isa",
    setCurrentAgent: () => {},
});

export const useAgents = () => useContext(AgentContext);

export const AgentProvider = ({ children }: { children: ReactNode }) => {
    const [agents, setAgents] = useState<AgentInfo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentAgent, setCurrentAgent] = useState<string>("isa");

    const client = useLogin ? useMsal().instance : undefined;

    const fetchAgents = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const token = client ? await getToken(client) : undefined;
            const response = await getAgentsApi(token);
            setAgents(response.agents);
        } catch (e: any) {
            console.error("[AgentContext] Failed to fetch agents:", e);
            setError(e.message || "Failed to fetch agents");
        } finally {
            setLoading(false);
        }
    }, [client]);

    useEffect(() => {
        fetchAgents();
    }, [fetchAgents]);

    return (
        <AgentContext.Provider value={{ agents, loading, error, refresh: fetchAgents, currentAgent, setCurrentAgent }}>
            {children}
        </AgentContext.Provider>
    );
};
